import { Card, Grid, makeStyles, Typography } from "@material-ui/core";
import { EMeasurementStatus } from "api/enums";
import clsx from "clsx";
import { PageLoading } from "components/UI/Page";
import { lang } from "i18nLang";
import { observer } from "mobx-react";
import { useEffect } from "react";
import { DashboardStore } from "store";
import { formatNumberToMonetary } from "utils";
import ContractDonutChart from "./ContractDonutChart";
import ContractLineChart from "./ContractLineChart";
import ContractLookerChart from "./ContractLookerChart";

const useStyle = makeStyles((theme) => ({
  container: {
    width: "100vw",
    padding: theme.spacing(8),
  },
  card: {
    width: "100%",
    padding: theme.spacing(2, 4),
    height: "fit-content",
  },
  cardDonut: { height: "382px !important" },
  labelContracts: {
    fontSize: "1.5rem",
    fontWeight: 700,
    position: "relative",
    top: -196,
    textAlign: "center",
  },
  containerValue: {
    "& > div": {
      padding: "0 0.5rem",
    },
  },
  cardValue: {
    width: "100%",
    border: `1px solid ${theme.palette.gray[300]}`,
    borderRadius: 8,
    padding: "0.5rem 1rem",
    marginTop: "1.5rem",
    marginBottom: "1.5rem",
  },
  titleValue: {
    fontSize: "1rem",
    fontWeight: 500,
    color: theme.palette.gray[500],
  },
  labelValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: theme.palette.gray[900],
  },
  loading: { height: 320 },
}));

const CardStatusTotal = ({ status, totals }) => {
  const classes = useStyle();

  return (
    <Grid item xs={6} md={3} lg={3}>
      <Grid
        className={classes.cardValue}
        container
        direction="column"
        alignItems="center"
      >
        <Typography className={classes.titleValue}>
          {lang(`dashboard.measureStatus.${status}`)}
        </Typography>
        <Typography className={classes.labelValue}>
          {formatNumberToMonetary(totals[status] || 0)}
        </Typography>
      </Grid>
    </Grid>
  );
};

const ContractsDashboard = observer(() => {
  const classes = useStyle();
  const {
    contractsCount,
    contractsByStatus,
    contractsBilling,
    measurementsPerMonth,
    measurementsTotalsByStatus,
    loadingMeasurePerMonth,
  } = DashboardStore;

  useEffect(() => {
    DashboardStore.setOpen(true);
    DashboardStore.setFiltersWithUserData();
    DashboardStore.filterAndSetFilteredContracts();
    DashboardStore.refreshContractsData();

    return () => {
      DashboardStore.reset();
    };
  }, []);

  return (
    <Grid
      className={classes.container}
      container
      justifyContent="space-evenly"
      alignItems="center"
      spacing={6}
    >
      <Grid container item xs={12} spacing={6}>
        <Grid container item xs={12} md={6} lg={6}>
          <Card className={clsx(classes.card, classes.cardDonut)}>
            <Typography variant="h6">{lang("dashboard.contracts")}</Typography>
            <ContractDonutChart series={contractsByStatus} />
            <Typography className={classes.labelContracts}>
              {contractsCount === 1
                ? lang("dashboard.oneContract")
                : lang("dashboard.nContracts", contractsCount)}
            </Typography>
          </Card>
        </Grid>
        <Grid container item xs={12} md={6} lg={6}>
          <Card className={clsx(classes.card, classes.cardDonut)}>
            <Typography variant="h6">{lang("dashboard.billing")}</Typography>
            <ContractLookerChart
              title={lang("dashboard.lookerTitle")}
              parameters={contractsBilling}
            />
          </Card>
        </Grid>
      </Grid>
      <Grid container item xs={12}>
        <Card className={classes.card}>
          <Typography variant="h6">{lang("dashboard.measurements")}</Typography>
          <Grid className={classes.containerValue} container item xs={12}>
            <CardStatusTotal
              status={EMeasurementStatus.ANDAMENTO}
              totals={measurementsTotalsByStatus}
            />
            <CardStatusTotal
              status={EMeasurementStatus.AGUARDANDO}
              totals={measurementsTotalsByStatus}
            />
            <CardStatusTotal
              status={EMeasurementStatus.APROVADA}
              totals={measurementsTotalsByStatus}
            />
            <CardStatusTotal
              status={EMeasurementStatus.FATURADA}
              totals={measurementsTotalsByStatus}
            />
          </Grid>
          {loadingMeasurePerMonth && (
            <PageLoading className={classes.loading} />
          )}
          {!loadingMeasurePerMonth && (
            <ContractLineChart data={measurementsPerMonth} />
          )}
        </Card>
      </Grid>
    </Grid>
  );
});

export default ContractsDashboard;
